/**
 * Media-library API client (web). Typed wrappers over the shared apiClient for
 * the playlist + library endpoints (specs/004-media-library). Custom-content,
 * upload, cover and trash endpoints are added in later Phase-1 slices.
 */
import { apiClient } from '../../../services/apiClient';
import type {
  AudioContentDto,
  AudioPlaylistDetailDto,
  AudioPlaylistDto,
  ContentQuotaDto,
  ContentStreamUrlDto,
  ContentTagsResponse,
  CreatePlaylistRequest,
  CustomContentFilterParams,
  LibraryCategoriesResponse,
  LibraryFilterParams,
  LibrarySearchParams,
  LibraryTrackDto,
  PaginatedResponse,
  PlaylistFilterParams,
  TrashContentDto,
  TrashFilterParams,
  UpdateContentRequest,
  UpdatePlaylistRequest,
} from '../types';

/** Build a query string, repeating array params (genres=a&genres=b) like the backend expects. */
export function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((v) => sp.append(key, String(v)));
    } else {
      sp.append(key, String(value));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// --- Playlists ---

export const listPlaylists = (params: PlaylistFilterParams = {}) =>
  apiClient.get<PaginatedResponse<AudioPlaylistDto>>(`/api/v1/playlists${qs(params)}`);

export const getPlaylistById = (id: string) =>
  apiClient.get<AudioPlaylistDetailDto>(`/api/v1/playlists/${id}`);

export const getRelatedPlaylists = (id: string, limit = 10) =>
  apiClient.get<AudioPlaylistDto[]>(`/api/v1/playlists/${id}/related?limit=${limit}`);

export const createPlaylist = (data: CreatePlaylistRequest) =>
  apiClient.post<AudioPlaylistDto>('/api/v1/playlists', data);

export const updatePlaylist = (id: string, data: UpdatePlaylistRequest) =>
  apiClient.put<AudioPlaylistDto>(`/api/v1/playlists/${id}`, data);

export const deletePlaylist = (id: string) =>
  apiClient.delete<void>(`/api/v1/playlists/${id}`);

export const duplicatePlaylist = (id: string) =>
  apiClient.post<AudioPlaylistDto>(`/api/v1/playlists/${id}/duplicate`);

// --- Favorites ---

export const getFavorites = () =>
  apiClient.get<{ playlistIds: string[] }>('/api/v1/playlists/favorites');

export const addFavorite = (id: string) =>
  apiClient.post<void>(`/api/v1/playlists/${id}/favorite`);

export const removeFavorite = (id: string) =>
  apiClient.delete<void>(`/api/v1/playlists/${id}/favorite`);

// --- Playlist tracks ---

export const addTrackToPlaylist = (playlistId: string, contentId: string) =>
  apiClient.post<AudioPlaylistDetailDto>(`/api/v1/playlists/${playlistId}/tracks`, { contentId });

export const removeTrackFromPlaylist = (playlistId: string, trackId: string) =>
  apiClient.delete<void>(`/api/v1/playlists/${playlistId}/tracks/${trackId}`);

export const duplicatePlaylistTrack = (playlistId: string, trackId: string) =>
  apiClient.post<AudioPlaylistDetailDto>(`/api/v1/playlists/${playlistId}/tracks/${trackId}/duplicate`);

export const reorderPlaylistTracks = (playlistId: string, trackItemIds: string[]) =>
  apiClient.put<void>(`/api/v1/playlists/${playlistId}/tracks/reorder`, { trackItemIds });

// --- Playlist cover image ---

export const uploadPlaylistCover = (playlistId: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiClient.upload<{ url: string }>(`/api/v1/playlists/${playlistId}/cover-image`, fd);
};

export const deletePlaylistCover = (playlistId: string) =>
  apiClient.delete<void>(`/api/v1/playlists/${playlistId}/cover-image`);

// --- Track sources (for the "add tracks" picker) ---

export const listLibraryTracks = (params: LibraryFilterParams = {}) =>
  apiClient.get<PaginatedResponse<LibraryTrackDto>>(`/api/v1/library/tracks${qs(params)}`);

export const searchLibraryTracks = (params: LibrarySearchParams) =>
  apiClient.get<PaginatedResponse<LibraryTrackDto>>(`/api/v1/library/tracks/search${qs(params)}`);

export const listCustomContent = (params: CustomContentFilterParams = {}) =>
  apiClient.get<PaginatedResponse<AudioContentDto>>(`/api/v1/content${qs(params)}`);

// --- Library taxonomy (filter options) ---

export const getLibraryFilterOptions = () =>
  apiClient.get<LibraryCategoriesResponse>('/api/v1/library/categories');

// --- Custom content (clips) ---

export const getContentTags = () =>
  apiClient.get<ContentTagsResponse>('/api/v1/content/tags');

export const getContentStreamUrl = (id: string) =>
  apiClient.get<ContentStreamUrlDto>(`/api/v1/content/${id}/stream-url`);

export const getContentQuota = () =>
  apiClient.get<ContentQuotaDto>('/api/v1/content/quota');

export const updateCustomContent = (id: string, data: UpdateContentRequest) =>
  apiClient.put<AudioContentDto>(`/api/v1/content/${id}`, data);

/**
 * Soft-delete a clip. On a 409 the apiClient throws `ApiError` whose `details`
 * is the {@link DeleteConflictResponse} (the clip is used by playlists); callers
 * re-call with `confirmDelete: true` to send `X-Confirm-Delete` and move it to trash.
 */
export const deleteCustomContent = (id: string, confirmDelete = false) =>
  apiClient.delete<void>(
    `/api/v1/content/${id}`,
    confirmDelete ? { headers: { 'X-Confirm-Delete': 'true' } } : undefined,
  );

export function uploadCustomContent(
  file: File,
  metadata: { title: string; tags?: string[] },
  onProgress?: (percent: number) => void,
): Promise<AudioContentDto> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('title', metadata.title);
  metadata.tags?.forEach((tag) => fd.append('tags', tag));
  return apiClient.uploadWithProgress<AudioContentDto>('/api/v1/content/upload', fd, onProgress);
}

// --- Trash ---

export const listTrashContent = (params: TrashFilterParams = {}) =>
  apiClient.get<PaginatedResponse<TrashContentDto>>(`/api/v1/content/trash${qs(params)}`);

export const restoreTrashContent = (id: string) =>
  apiClient.post<AudioContentDto>(`/api/v1/content/trash/${id}/restore`);
